import { Collection, Database } from '@nozbe/watermelondb';

import EntryUser from '../../EntryUser';
import InventoryChange from '../../InventoryChange';
import InventoryProduct from '../../InventoryProduct';
import Organisation from '../../Organisation';
import Procedure from '../../Procedure';
import {
    DBServiceOptionsWithImagesAndValidation,
    DBServiceOptionsWithImagesLoggerAndValidation,
} from '../../../../types/dbService';
import {
    EntryModel,
    EntryPayload,
    EntryProcedureToAdd,
    EntryProductToAdd,
    PreparedDataOnEntryAdd,
} from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { EntryProductModel } from '../../../../types/EntryProduct';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { InventoryProductModel } from '../../../../types/InventoryProduct';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { toPreparedDataItems } from './common';

const prepareCreateEntry = async ({
    payload,
    userId,
    serviceOptions,
    collection,
}: {
    payload: EntryPayload;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    collection: Collection<EntryModel>;
}) => {
    const organisationService = new Organisation(serviceOptions);
    const organisation = await organisationService.get();
    const { id: organisationID } = organisation[0];

    return collection.prepareCreate((entry) => {
        entry.horse.id = payload.horseId;
        entry.eventId = payload.eventId;
        entry.invoiceId = payload.invoiceId;
        entry.loggedTime = payload.loggedTime;
        entry.title = payload.title;
        entry.notes = payload.notes;
        entry.privateNotes = payload.privateNotes;
        entry.userId = userId;
        entry.organisationId = organisationID;
    });
};

const prepareEntryProducts = async ({
    createdEntry,
    entryProducts,
    userId,
    serviceOptions,
    database,
}: {
    createdEntry: EntryModel;
    entryProducts: (EntryProductToAdd | EntryProductModel)[];
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    database: Database;
}) => {
    const entryProductsToAdd: EntryProductModel[] = [];
    const inventoryProductsToAdd: InventoryProductModel[] = [];
    const inventoryChanges: InventoryChangeModel[] = [];

    if (createdEntry && entryProducts.length > 0) {
        const inventoryProductService = new InventoryProduct(serviceOptions);
        const inventoryChangesService = new InventoryChange(serviceOptions);

        const entryProductsCollection =
            database.collections.get<EntryProductModel>('entry_products');
        const inventoryProductsCollection =
            database.collections.get<InventoryProductModel>(
                'inventory_products',
            );

        await Promise.all(
            entryProducts.map(async (entryProduct) => {
                const inventoryProduct =
                    await inventoryProductService.getByParam(
                        'product_id',
                        entryProduct.productId,
                    );

                if (inventoryProduct.length === 1) {
                    const inventoryChange =
                        await inventoryChangesService.prepareAdd(
                            {
                                inventoryProductId: inventoryProduct[0].id,
                                quantityChange: -entryProduct.quantity,
                                processAt: new Date(createdEntry.loggedTime),
                            },
                            userId,
                        );

                    inventoryChanges.push(inventoryChange);
                } else {
                    const inventoryProductToAdd =
                        await inventoryProductsCollection.prepareCreate(
                            (inventoryProd) => {
                                inventoryProd.productId =
                                    entryProduct.productId;
                                inventoryProd.userId = createdEntry.userId;
                                inventoryProd.organisationId =
                                    createdEntry.organisationId;
                                inventoryProd.quantity = 0;
                                inventoryProd.favourite = false;
                            },
                        );

                    inventoryProductsToAdd.push(inventoryProductToAdd);

                    const inventoryChange =
                        await inventoryChangesService.prepareAdd(
                            {
                                inventoryProductId: inventoryProductToAdd.id,
                                quantityChange: -entryProduct.quantity,
                                processAt: new Date(createdEntry.loggedTime),
                            },
                            userId,
                        );

                    inventoryChanges.push(inventoryChange);
                }

                const entryProductToAdd =
                    await entryProductsCollection.prepareCreate((entryProd) => {
                        entryProd.entryId = createdEntry.id;
                        entryProd.productId = entryProduct.productId;
                        entryProd.quantity = entryProduct.quantity;
                        entryProd.userId = createdEntry.userId;
                        entryProd.organisationId = createdEntry.organisationId;
                    });

                entryProductsToAdd.push(entryProductToAdd);
            }),
        );
    }

    return {
        entryProductsToAdd,
        inventoryProductsToAdd,
        inventoryChanges,
    };
};

const prepareEntryProcedures = async ({
    createdEntry,
    entryProcedures,
    serviceOptions,
    database,
}: {
    createdEntry: EntryModel;
    entryProcedures: (EntryProcedureToAdd | EntryProcedureModel)[];
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    database: Database;
}) => {
    const entryProceduresToAdd: EntryProcedureModel[] = [];

    if (createdEntry && entryProcedures.length > 0) {
        const proceduresService = new Procedure(serviceOptions);

        const entryProceduresCollection =
            database.collections.get<EntryProcedureModel>('entry_procedures');

        await Promise.all(
            entryProcedures.map(async (entryProcedure) => {
                const procedure = await proceduresService.getByID(
                    entryProcedure.procedureId,
                );

                if (!!procedure) {
                    const entryProcedureToAdd =
                        await entryProceduresCollection.prepareCreate(
                            (entryProc) => {
                                entryProc.entryId = createdEntry.id;
                                entryProc.procedureId =
                                    entryProcedure.procedureId;
                                entryProc.price = entryProcedure.price ?? '';
                                entryProc.quantity = entryProcedure.quantity;
                                entryProc.name = procedure.name;
                                entryProc.userId = createdEntry.userId;
                                entryProc.organisationId =
                                    createdEntry.organisationId;
                            },
                        );

                    entryProceduresToAdd.push(entryProcedureToAdd);
                }
            }),
        );
    }

    return { entryProceduresToAdd };
};

const prepareEntryUsers = async ({
    createdEntry,
    membersIds,
    serviceOptions,
}: {
    createdEntry: EntryModel;
    membersIds: string[];
    serviceOptions: DBServiceOptionsWithImagesLoggerAndValidation;
}) => {
    const entryUserService = new EntryUser(serviceOptions);
    const entryUsersToAdd = await entryUserService.prepareAddBatch(
        membersIds,
        createdEntry.id,
    );

    return { entryUsersToAdd };
};

const appendPreparedEntryProducts = (
    preparedData: PreparedDataOnEntryAdd[],
    productsData: Awaited<ReturnType<typeof prepareEntryProducts>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            productsData.inventoryProductsToAdd,
            'inventory_products',
            'add',
        ),
        ...toPreparedDataItems(
            productsData.entryProductsToAdd,
            'entry_products',
            'add',
        ),
        ...toPreparedDataItems(
            productsData.inventoryChanges,
            'inventory_changes',
            'add',
        ),
    ];
};

const appendPreparedEntryProcedures = (
    preparedData: PreparedDataOnEntryAdd[],
    proceduresData: Awaited<ReturnType<typeof prepareEntryProcedures>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            proceduresData.entryProceduresToAdd,
            'entry_procedures',
            'add',
        ),
    ];
};

const appendPreparedEntryUsers = (
    preparedData: PreparedDataOnEntryAdd[],
    usersData: Awaited<ReturnType<typeof prepareEntryUsers>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(usersData.entryUsersToAdd, 'entry_users', 'add'),
    ];
};

const logDBActions = ({
    createdEntry,
    productsData,
    proceduresData,
    usersData,
    logDBAction,
    tableName,
}: {
    createdEntry: EntryModel;
    productsData: Awaited<ReturnType<typeof prepareEntryProducts>>;
    proceduresData: Awaited<ReturnType<typeof prepareEntryProcedures>>;
    usersData?: Awaited<ReturnType<typeof prepareEntryUsers>>;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Create entry',
        modelName: tableName,
        payload: createdEntry,
    });
    logDBAction({
        message: 'Create entry - create entry products',
        modelName: tableName,
        payload: productsData.entryProductsToAdd,
    });
    logDBAction({
        message: 'Create entry - create entry procedures',
        modelName: tableName,
        payload: proceduresData.entryProceduresToAdd,
    });
    logDBAction({
        message: 'Create entry - add entry users',
        modelName: tableName,
        payload: usersData?.entryUsersToAdd ?? [],
    });
};

export {
    appendPreparedEntryProcedures,
    appendPreparedEntryProducts,
    appendPreparedEntryUsers,
    logDBActions,
    prepareCreateEntry,
    prepareEntryProcedures,
    prepareEntryProducts,
    prepareEntryUsers,
};
